import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button } from '@kcm/ui'
import { ErrorCode } from '@kcm/contracts'
import { useTranslate } from '@tolgee/react'
import { ApiError } from '../lib/api'
import { endpoints } from '../lib/endpoints'
import { useLocale } from '../hooks/useLocale'
import { androidEscapeUrl, detectWebview } from '../lib/webview'
import { KycOutcome } from '../components/KycOutcome'

/**
 * Step 2 — identity (PLAN.md §11.1, §11.2, §15).
 *
 * This screen exists to make the handoff to Didit survivable. The legacy funnel
 * redirected straight into the camera flow with no warning, from inside
 * whatever browser the user happened to be in.
 *
 * Two things happen before the redirect:
 *
 *   1. **The in-app browser is called out**, because it is the one condition
 *      that reliably breaks the camera and cannot be fixed once they are inside
 *      Didit's flow.
 *   2. **Expectations are set** — what document, how long, what the camera is
 *      for. §11.2: people abandon a permission prompt they were not expecting
 *      far more readily than one they were told about ten seconds earlier.
 *
 * The residential address is no longer asked here: Didit reads it (and the
 * legal name) off the document, so both are confirmed on the screen *after*
 * the handoff, prefilled — a lower-friction ask than typing them first.
 */
export function StepIdentity() {
  const navigate = useNavigate()
  const { t } = useTranslate()
  const locale = useLocale()
  const webview = useMemo(() => detectWebview(), [])

  // Reflects the durable state, so someone who already finished in another tab
  // is not asked to do it again.
  const status = useQuery({
    queryKey: ['kyc-status'],
    queryFn: ({ signal }) => endpoints.kyc.status(signal),
    staleTime: 10_000,
  })

  const start = useMutation({
    // Didit fixes the hosted flow's language at creation, so it has to be the
    // one on screen right now, not the one step 1 was submitted in.
    mutationFn: async () => endpoints.kyc.start({ locale }),
    onSuccess: (res) => {
      // Full page navigation, not a new tab: an in-app browser frequently
      // ignores window.open, and a popup blocker would swallow it silently.
      if (res.url) window.location.assign(res.url)
    },
  })

  const error = start.error instanceof ApiError ? start.error : null

  // Step 1 never completed, so there is no CRM client for Didit to key on.
  if (error?.code === ErrorCode.KYC_NOT_READY) {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="error" title={t('identity.notReadyTitle')}>
          {error.message}
        </Alert>
        <Button onClick={() => navigate('/', { replace: true })}>{t('identity.backToStep1')}</Button>
      </div>
    )
  }

  const state = status.data?.state
  if (state && state !== 'not_started' && state !== 'in_progress') {
    return <KycOutcome state={state} reason={status.data?.reason} />
  }

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (!start.isPending) start.mutate()
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-brand">{t('identity.title')}</h1>
        <p className="mt-1 text-sm text-muted">{t('identity.subtitle')}</p>
      </div>

      {webview.isInApp && <WebviewWarning app={webview.app} platform={webview.platform} />}

      {/* A field-level failure is already shown on the field itself; repeating
          it in the banner would say the same thing twice. */}
      {error && error.code !== ErrorCode.VALIDATION_FAILED && (
        <Alert tone="error" title={t('identity.startFailedTitle')}>
          {error.message}
        </Alert>
      )}

      {/* Didit is down, or has no workflow configured for this site. That is our
          problem, not the applicant's, and it used to end their application
          here — a vendor outage stranding every sign-up on step 2. They can
          finish the rest now and we collect the document afterwards. */}
      {error?.code === ErrorCode.KYC_UNAVAILABLE && (
        <>
          <Button variant="ghost" onClick={() => navigate('/confirm', { replace: true })}>
            {t('kyc.continueAnyway')}
          </Button>
          <p className="text-sm text-muted">{t('kyc.finishLaterBody')}</p>
        </>
      )}

      <ul className="flex flex-col gap-3 rounded-xl border border-line bg-white p-5 text-sm">
        <li className="flex gap-3">
          <span aria-hidden="true">1</span>
          <span>{t('identity.step1')}</span>
        </li>
        <li className="flex gap-3">
          <span aria-hidden="true">2</span>
          <span>{t('identity.step2')}</span>
        </li>
        <li className="flex gap-3">
          <span aria-hidden="true">3</span>
          <span>{t('identity.step3')}</span>
        </li>
      </ul>

      <Button type="submit" disabled={start.isPending}>
        {start.isPending ? t('identity.starting') : t('identity.start')}
      </Button>

      <p className="text-xs text-muted">{t('identity.partnerNote')}</p>

      {state === 'in_progress' && (
        <Alert tone="info">{t('identity.inProgress')}</Alert>
      )}
    </form>
  )
}

function WebviewWarning({
  app,
  platform,
}: {
  app?: string | undefined
  platform: 'ios' | 'android' | 'other'
}) {
  const { t } = useTranslate()
  const named = app ? t('webview.namedBrowser', { app }) : t('webview.thisBrowser')

  return (
    <Alert tone="info" title={t('webview.title')}>
      <p>{t('webview.cameraWarn', { browser: named })}</p>
      {platform === 'ios' ? (
        <p className="mt-2">{t('webview.iosInstruction')}</p>
      ) : platform === 'android' ? (
        <p className="mt-2">
          <a className="font-medium underline" href={androidEscapeUrl(window.location.href)}>
            {t('webview.openInChrome')}
          </a>{' '}
          {t('webview.androidInstruction')}
        </p>
      ) : (
        <p className="mt-2">{t('webview.otherInstruction')}</p>
      )}
    </Alert>
  )
}
